import { type ReactNode } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/cn";
import { statusToTone, type Tone } from "@/lib/status";

/**
 * Badge — THE single status/label chip (DESIGN-SYSTEM §5, §9.2).
 *
 * Subtle backgrounds use Tailwind alpha modifiers on the role token
 * (`bg-<tone>/15` + `text-<tone>`) so there's one color source per tone and it
 * themes automatically. Neutral renders on the elevated surface per §1.2.
 */
const badgeVariants = cva(
  "inline-flex items-center gap-1 rounded-full font-medium whitespace-nowrap " +
    "px-2 py-0.5 text-xs leading-tight",
  {
    variants: {
      tone: {
        success: "",
        warning: "",
        danger: "",
        info: "",
        neutral: "",
        accent: "",
      },
      variant: {
        solid: "",
        subtle: "",
        outline: "border bg-transparent",
      },
    },
    compoundVariants: [
      // solid
      { tone: "success", variant: "solid", class: "bg-success text-fg-on-accent" },
      { tone: "warning", variant: "solid", class: "bg-warning text-fg-on-accent" },
      { tone: "danger", variant: "solid", class: "bg-danger text-fg-on-danger" },
      { tone: "info", variant: "solid", class: "bg-info text-fg-on-accent" },
      { tone: "neutral", variant: "solid", class: "bg-bg-elevated text-fg-primary" },
      { tone: "accent", variant: "solid", class: "bg-accent text-fg-on-accent" },
      // subtle (default)
      { tone: "success", variant: "subtle", class: "bg-success/15 text-success" },
      { tone: "warning", variant: "subtle", class: "bg-warning/15 text-warning" },
      { tone: "danger", variant: "subtle", class: "bg-danger/15 text-danger" },
      { tone: "info", variant: "subtle", class: "bg-info/15 text-info" },
      { tone: "neutral", variant: "subtle", class: "bg-bg-elevated text-fg-muted" },
      { tone: "accent", variant: "subtle", class: "bg-accent/15 text-accent" },
      // outline
      { tone: "success", variant: "outline", class: "border-success/40 text-success" },
      { tone: "warning", variant: "outline", class: "border-warning/40 text-warning" },
      { tone: "danger", variant: "outline", class: "border-danger/40 text-danger" },
      { tone: "info", variant: "outline", class: "border-info/40 text-info" },
      { tone: "neutral", variant: "outline", class: "border-border text-fg-muted" },
      { tone: "accent", variant: "outline", class: "border-accent/40 text-accent" },
    ],
    defaultVariants: {
      tone: "neutral",
      variant: "subtle",
    },
  },
);

/** The colored leading dot inherits the badge's text color. */
const dotTone: Record<Tone, string> = {
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-danger",
  info: "bg-info",
  neutral: "bg-fg-muted",
  accent: "bg-accent",
};

export interface BadgeProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, "color">,
    VariantProps<typeof badgeVariants> {
  /** Render a leading status dot. */
  dot?: boolean;
  /** Optional leading icon (lucide, ~12px). */
  icon?: ReactNode;
  children?: ReactNode;
}

export function Badge({
  tone = "neutral",
  variant = "subtle",
  dot = false,
  icon,
  className,
  children,
  ...props
}: BadgeProps) {
  return (
    <span className={cn(badgeVariants({ tone, variant }), className)} {...props}>
      {dot && (
        <span
          aria-hidden="true"
          className={cn("h-1.5 w-1.5 rounded-full", dotTone[tone ?? "neutral"])}
        />
      )}
      {icon}
      {children}
    </span>
  );
}

export interface StatusBadgeProps extends Omit<BadgeProps, "tone" | "children"> {
  /** Status domain — see DESIGN-SYSTEM §1.2 (account, channel, campaign, ...). */
  domain: string;
  /** Raw status value (e.g. "active", "banned"). */
  value: string;
  /** Display label; falls back to the raw value when omitted. */
  label?: string;
}

/** Status chip whose tone is resolved from (domain, value) via {@link statusToTone}. */
export function StatusBadge({ domain, value, label, ...props }: StatusBadgeProps) {
  return (
    <Badge tone={statusToTone(domain, value)} {...props}>
      {label ?? value}
    </Badge>
  );
}
